import { type UseQueryOptions } from 'react-query'

import { apiPaths, queryKeyWithNpsn } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi } from 'api/kinerja/utils/api'

import type { KinerjaTipe } from 'types/kinerja'
import type { School, TabFilterRoles } from 'types/kinerja-ks'
import type {
  PelaksanaanState,
  PengawasSchoolCount,
  PengawasSchoolDetail,
  PengawasSchoolMeta,
  PengawasSchools,
} from 'types/kinerja-ks-pelaksanaan'
import type { CommonResponse } from 'types/api'

export const API_GET_PS_SCHOOLS_QUERY_KEY = 'API_GET_PS_SCHOOLS_QUERY_KEY'
export const API_GET_PS_SCHOOL_COUNT_QUERY_KEY =
  'API_GET_PS_SCHOOL_COUNT_QUERY_KEY'
export const API_GET_KS_SCHOOL_DETAIL_BY_PS_QUERY_KEY =
  'API_GET_KS_SCHOOL_DETAIL_BY_PS_QUERY_KEY'

type ParamsGetSchoolList = {
  page?: number
  pageSize?: number
  name?: string
  observationStatus?: Array<PelaksanaanState>
  role?: TabFilterRoles
  tipe?: KinerjaTipe
}

/**
 * Returns the query keys for clearing the cache of the useApiGetSchoolList query.
 * @param params - The parameters for the query.
 * @returns An array of query keys.
 */
export function getSchoolListQueryKeys(params: ParamsGetSchoolList) {
  return [
    API_GET_PS_SCHOOLS_QUERY_KEY,
    params?.page || 1,
    params?.pageSize || 10,
    params?.name || '',
    params?.observationStatus?.join('-') || '',
    params?.role || '',
    params?.tipe || '',
  ]
}

/**
 * Fetches the list of schools
 *
 * @param params - The parameters for the API request.
 * @param opts - The options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export function useApiGetSchoolList(
  params: ParamsGetSchoolList,
  opts?: UseQueryOptions<CommonResponse<PengawasSchools, PengawasSchoolMeta>>
) {
  const page = params?.page || 1
  const pageSize = params?.pageSize || 10
  const filterName = params?.name || undefined
  const filterStatus = params?.observationStatus || undefined
  const role = params?.role
  const tipe = params?.tipe

  const path = `${apiPaths.pelaksanaan}/schools`

  return useKinerjaApi<CommonResponse<PengawasSchools, PengawasSchoolMeta>>(
    path,
    {
      axiosConfig: {
        params: {
          page,
          page_size: pageSize,
          name: filterName,
          observation_status: filterStatus,
          role,
          tipe,
        },
      },
      queryKey: getSchoolListQueryKeys(params),
      queryConfig: {
        enabled: false,
        retry: false,
        useErrorBoundary: false,
        keepPreviousData: true,
        ...opts,
      },
    }
  )
}

/**
 * Fetches the number of schools
 *
 * @param opts - The options for the query.
 * @returns The result of the API call.
 */
export function useApiGetSchoolCount(
  opts?: UseQueryOptions<CommonResponse<PengawasSchoolCount>>
) {
  return useKinerjaApi<CommonResponse<PengawasSchoolCount>>(
    `${apiPaths.pelaksanaan}/schools-count`,
    {
      queryKey: API_GET_PS_SCHOOL_COUNT_QUERY_KEY,
      queryConfig: {
        enabled: false,
        useErrorBoundary: false,
        ...opts,
      },
    }
  )
}

export function useApiGetSchoolDetail(
  params: { npsn: School['npsn'] },
  opts?: UseQueryOptions<PengawasSchoolDetail>
) {
  return useKinerjaApi<PengawasSchoolDetail>(
    `${apiPaths.pelaksanaan}/schools/details`,
    {
      queryKey: queryKeyWithNpsn(
        API_GET_KS_SCHOOL_DETAIL_BY_PS_QUERY_KEY,
        params?.npsn
      ),
      axiosConfig: { params },
      queryConfig: {
        enabled: false,
        useErrorBoundary: false,
        retry: false,
        ...opts,
      },
    }
  )
}
